import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { AlertCircle, ArrowLeft, CheckCircle2, CloudUpload, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppDialog } from "@/components/common/AppDialog";
import { materialApi } from "@/lib/api/services/material.api";
import {
  MATERIAL_IMPORT_FIELDS,
  guessColumnMapping,
  parseSpreadsheetFile,
  toBulkItems,
  validateImportRows,
  type ColumnMapping,
  type ParsedSheet,
  type ValidatedImportRow,
} from "@/lib/import/materials-import";
import type { BulkImportError } from "@/lib/api/services/material.api";

/**
 * The T-1201 import wizard: drop a supplier's Excel/CSV → say which column is
 * which → see exactly which rows will import and why the rest won't → commit.
 *
 * The preview is the contract: a row is either in the will-import list or has
 * its errors printed next to it. Nothing is dropped silently, and nothing is
 * written until the user has seen that split — the dry-run the preview makes
 * runs every server-side check (including "already exists", which is also what
 * makes re-importing the same file safe) without creating anything.
 */

type Step = "upload" | "mapping" | "preview" | "done";

/** "Not mapped" — Radix Select reserves the empty string, so a sentinel. */
const UNMAPPED = "__unmapped__";

interface ImportIngredientsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete?: () => void;
}

export function ImportIngredientsDialog({
  open,
  onOpenChange,
  onImportComplete,
}: ImportIngredientsDialogProps) {
  const { i18n, t } = useLingui();
  const inputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>("upload");
  const [fileName, setFileName] = useState("");
  const [sheet, setSheet] = useState<ParsedSheet | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [rows, setRows] = useState<ValidatedImportRow[]>([]);
  /** Rows the dry run rejected, keyed by row index — usually "already exists". */
  const [serverErrors, setServerErrors] = useState<Map<number, string>>(new Map());
  const [isChecking, setIsChecking] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [result, setResult] = useState<{ created: number; failed: BulkImportError[] } | null>(null);

  const { data: allergens = [] } = useQuery({
    queryKey: ["allergens"],
    queryFn: () => materialApi.getAllergens(),
    enabled: open,
  });

  const allergenCodes = useMemo(
    () => new Set(allergens.map((a) => a.code.toUpperCase())),
    [allergens],
  );

  const reset = () => {
    setStep("upload");
    setFileName("");
    setSheet(null);
    setMapping({});
    setRows([]);
    setServerErrors(new Map());
    setIsChecking(false);
    setIsImporting(false);
    setResult(null);
  };

  const handleOpenChange = (value: boolean) => {
    if (!value) reset();
    onOpenChange(value);
  };

  // ── Step transitions ────────────────────────────────────────────────────────

  const handleFile = async (file: File) => {
    try {
      const parsed = await parseSpreadsheetFile(file);
      setFileName(file.name);
      setSheet(parsed);
      setMapping(guessColumnMapping(parsed.headers));
      setStep("mapping");
    } catch {
      toast.error(t`Could not read the file. It should be an Excel sheet or a CSV.`);
    }
  };

  const runPreview = async () => {
    if (!sheet) return;
    const validated = validateImportRows(sheet.rows, mapping, { allergenCodes });
    setRows(validated);
    setStep("preview");

    // Ask the API what a real run would do — nothing is written. Failures come
    // back indexed into the payload; walk the valid rows in the same order to
    // pin each one back to its spreadsheet row.
    const validRows = validated.filter((row) => row.item !== null);
    if (validRows.length === 0) return;
    setIsChecking(true);
    try {
      const dryRun = await materialApi.bulkCreate(toBulkItems(validated), undefined, true);
      const errors = new Map<number, string>();
      for (const failure of dryRun.failed) {
        const row = validRows[failure.index];
        if (row) errors.set(row.index, failure.error);
      }
      setServerErrors(errors);
    } catch {
      // The preview still shows the client-side checks; the server ones will
      // simply surface at commit instead. Say so rather than blocking.
      toast.warning(t`Could not check for existing ingredients — duplicates will be reported when you import.`);
    } finally {
      setIsChecking(false);
    }
  };

  const handleImport = async () => {
    const items = toBulkItems(rows);
    if (items.length === 0) return;
    setIsImporting(true);
    try {
      const commit = await materialApi.bulkCreate(items);
      setResult(commit);
      setStep("done");
      if (commit.created > 0) onImportComplete?.();
    } catch {
      toast.error(t`Import failed. Nothing was saved — please try again.`);
    } finally {
      setIsImporting(false);
    }
  };

  // ── Derived counts ──────────────────────────────────────────────────────────

  const requiredMapped = MATERIAL_IMPORT_FIELDS.filter((f) => f.required).every(
    (f) => mapping[f.key],
  );
  const errorRows = rows.filter((row) => row.errors.length > 0 || serverErrors.has(row.index));
  const readyRows = rows.filter((row) => row.errors.length === 0 && !serverErrors.has(row.index));
  const validCount = rows.filter((row) => row.item !== null).length;

  const stepTitle: Record<Step, string> = {
    upload: i18n._("Import ingredients"),
    mapping: i18n._("Match your columns"),
    preview: i18n._("Review before importing"),
    done: i18n._("Import finished"),
  };

  const stepDescription: Record<Step, string | undefined> = {
    upload: i18n._("Upload an Excel or CSV ingredient list — a supplier export works as-is."),
    mapping: i18n._("Say which column holds which detail. Name and purchase unit are required; leave anything else unmapped to skip it."),
    preview: undefined,
    done: undefined,
  };

  // ── Footers ─────────────────────────────────────────────────────────────────

  const mappingFooter = (
    <div className="flex w-full items-center justify-between gap-3">
      <Button variant="outline" onClick={() => setStep("upload")} className="gap-2">
        <ArrowLeft className="size-4" />
        {i18n._("Back")}
      </Button>
      <Button onClick={runPreview} disabled={!requiredMapped}>
        {i18n._("Continue to review")}
      </Button>
    </div>
  );

  const previewFooter = (
    <div className="flex w-full items-center justify-between gap-3">
      <Button
        variant="outline"
        onClick={() => {
          setServerErrors(new Map());
          setStep("mapping");
        }}
        disabled={isImporting}
        className="gap-2"
      >
        <ArrowLeft className="size-4" />
        {i18n._("Back to columns")}
      </Button>
      <Button onClick={handleImport} disabled={isImporting || validCount === 0} className="gap-2">
        {isImporting ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <CloudUpload className="size-4" />
        )}
        {isImporting
          ? i18n._("Importing…")
          : i18n._(t`Import ${validCount} ingredients`)}
      </Button>
    </div>
  );

  const doneFooter = (
    <div className="flex w-full justify-end">
      <Button onClick={() => handleOpenChange(false)}>{i18n._("Close")}</Button>
    </div>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={stepTitle[step]}
      description={stepDescription[step]}
      className={step === "preview" || step === "mapping" ? "max-w-3xl" : undefined}
      hideClose={isImporting}
      footer={
        step === "mapping" ? mappingFooter : step === "preview" ? previewFooter : step === "done" ? doneFooter : undefined
      }
    >
      {/* ── Upload ─────────────────────────────────────────────────────────── */}
      {step === "upload" && (
        <Button
          variant="ghost"
          className="flex h-auto w-full flex-col items-center gap-4 rounded-xl border-2 border-dashed border-(--color-border) bg-(--color-surface-muted)/40 px-6 py-14 text-center hover:bg-(--color-surface-muted)/60"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            if (file) void handleFile(file);
          }}
        >
          <div className="flex size-18 items-center justify-center rounded-full border border-(--color-border) bg-(--color-surface-page) shadow-sm">
            <FileSpreadsheet className="size-8 text-(--color-text-secondary)" />
          </div>
          <div>
            <p className="text-base font-medium">{i18n._("Drop your ingredient list here")}</p>
            <p className="mt-1 text-sm text-(--color-text-secondary)">
              {i18n._("Excel (.xlsx) or CSV — or click to browse.")}
            </p>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
              e.target.value = "";
            }}
          />
        </Button>
      )}

      {/* ── Column mapping ─────────────────────────────────────────────────── */}
      {step === "mapping" && sheet && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2 rounded-[0.625rem] border border-(--color-border) bg-(--color-surface-muted)/50 px-4 py-2.5 text-sm">
            <FileSpreadsheet className="size-4 shrink-0 text-(--color-text-secondary)" />
            <span className="font-medium">{fileName}</span>
            <span className="text-(--color-text-secondary)">
              {i18n._(t`${sheet.rows.length} rows`)}
            </span>
          </div>

          <div className="grid max-h-100 grid-cols-1 gap-x-8 gap-y-3 overflow-y-auto pr-1 sm:grid-cols-2">
            {MATERIAL_IMPORT_FIELDS.map((field) => (
              <div key={field.key} className="flex items-center justify-between gap-3">
                <span className="text-sm">
                  {i18n._(field.label)}
                  {field.required && <span className="text-(--color-danger)"> *</span>}
                </span>
                <Select
                  value={mapping[field.key] ?? UNMAPPED}
                  onValueChange={(value) =>
                    setMapping((prev) => {
                      const next: ColumnMapping = { ...prev };
                      if (value === UNMAPPED) delete next[field.key];
                      else next[field.key] = value;
                      return next;
                    })
                  }
                >
                  <SelectTrigger className="h-9 w-52">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNMAPPED}>
                      <span className="text-(--color-text-secondary)">{i18n._("Not mapped")}</span>
                    </SelectItem>
                    {sheet.headers.map((header) => (
                      <SelectItem key={header} value={header}>
                        {header}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Validation preview ─────────────────────────────────────────────── */}
      {step === "preview" && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-4 rounded-[0.625rem] border border-(--color-border) bg-(--color-surface-muted)/50 px-4 py-2.5 text-sm">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-emerald-600" />
              {i18n._(t`${readyRows.length} rows will import`)}
            </span>
            <span className="flex items-center gap-1.5">
              <AlertCircle className="size-4 text-(--color-danger)" />
              {i18n._(t`${errorRows.length} rows have problems and will be skipped`)}
            </span>
            {isChecking && (
              <span className="flex items-center gap-1.5 text-(--color-text-secondary)">
                <Loader2 className="size-4 animate-spin" />
                {i18n._("Checking for existing ingredients…")}
              </span>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto rounded-[0.625rem] border border-(--color-border)">
            {rows.map((row) => {
              const serverError = serverErrors.get(row.index);
              const problems = serverError ? [...row.errors, serverError] : row.errors;
              const ok = problems.length === 0;
              return (
                <div
                  key={row.index}
                  className="flex items-start gap-3 border-b border-(--color-border) px-4 py-2.5 text-sm last:border-b-0"
                >
                  <span className="w-14 shrink-0 tabular-nums text-(--color-text-secondary)">
                    {i18n._(t`Row ${row.rowNumber}`)}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-medium">{row.name}</span>
                  {ok ? (
                    <CheckCircle2 className="size-4 shrink-0 text-emerald-600" />
                  ) : (
                    <span className="flex min-w-0 max-w-[55%] items-start gap-1.5 text-(--color-danger)">
                      <AlertCircle className="mt-0.5 size-4 shrink-0" />
                      <span>{problems.join(" · ")}</span>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Result ─────────────────────────────────────────────────────────── */}
      {step === "done" && result && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 rounded-[0.625rem] border border-(--color-border) bg-(--color-surface-muted)/50 px-4 py-3">
            <CheckCircle2 className="size-5 shrink-0 text-emerald-600" />
            <p className="text-sm">
              {i18n._(t`${result.created} ingredients imported.`)}{" "}
              {result.failed.length > 0 && i18n._(t`${result.failed.length} could not be imported:`)}
            </p>
          </div>

          {result.failed.length > 0 && (
            <div className="max-h-64 overflow-y-auto rounded-[0.625rem] border border-(--color-border)">
              {result.failed.map((failure) => (
                <div
                  key={`${failure.index}-${failure.name}`}
                  className="flex items-start gap-3 border-b border-(--color-border) px-4 py-2.5 text-sm last:border-b-0"
                >
                  <AlertCircle className="mt-0.5 size-4 shrink-0 text-(--color-danger)" />
                  <span className="min-w-0 flex-1 truncate font-medium">{failure.name}</span>
                  <span className="max-w-[55%] text-(--color-text-secondary)">{failure.error}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </AppDialog>
  );
}
